import { useState, useCallback } from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  useInfiniteUpcomingEvents,
  useInfinitePastEvents,
  useInfiniteGoingEvents,
} from '@/lib/hooks/useEvents';
import { useAuth } from '@/lib/hooks/useAuth';
import { useUserProfile } from '@/lib/hooks/useUserProfile';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { PlusIcon } from '@phosphor-icons/react';
import { EventsSearch } from '@/components/events/EventsSearch';
import { EventsList } from '@/components/events/EventsList';
import { EventsListSkeleton } from '@/components/events/EventsListSkeleton';
import { useEventsListScrollRestoration } from '@/lib/hooks/useEventsListScrollRestoration';
import type { EventsListScrollTab } from '@/lib/types/events-scroll.types';
import {
  getAppScrollContainer,
  getCurrentHistoryIndex,
  writeEventsListScrollSnapshot,
} from '@/lib/utils/events-scroll.utils';
import { Route } from '@/routes/_authenticated/events.index';
import type { Event } from '@/lib/types/database';

/**
 * Events page - Container/orchestrator component.
 * Manages state and data fetching, delegates rendering to presentational components.
 */
export function Events() {
  const { user } = useAuth();
  const { isAdmin } = useUserProfile();
  const navigate = useNavigate();
  const { tab: activeTab } = Route.useSearch();

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Event[]>([]);
  const [isSearchLoading, setIsSearchLoading] = useState(false);

  const {
    data: upcomingEventsData,
    isLoading: isLoadingUpcoming,
    fetchNextPage: fetchNextUpcoming,
    hasNextPage: hasNextUpcoming,
    isFetchingNextPage: isFetchingNextUpcoming,
  } = useInfiniteUpcomingEvents();

  const {
    data: pastEventsData,
    isLoading: isLoadingPast,
    fetchNextPage: fetchNextPast,
    hasNextPage: hasNextPast,
    isFetchingNextPage: isFetchingNextPast,
  } = useInfinitePastEvents();

  const {
    data: goingEventsData,
    isLoading: isLoadingGoing,
    fetchNextPage: fetchNextGoing,
    hasNextPage: hasNextGoing,
    isFetchingNextPage: isFetchingNextGoing,
  } = useInfiniteGoingEvents(user?.id || null);

  // Flatten pages arrays
  const upcomingEvents =
    upcomingEventsData?.pages.flatMap((page) => page.data) || [];
  const pastEvents = pastEventsData?.pages.flatMap((page) => page.data) || [];
  const goingEvents = goingEventsData?.pages.flatMap((page) => page.data) || [];
  const upcomingPageCount = upcomingEventsData?.pages.length || 0;
  const pastPageCount = pastEventsData?.pages.length || 0;
  const goingPageCount = goingEventsData?.pages.length || 0;

  const isLoading =
    activeTab === 'upcoming'
      ? isLoadingUpcoming
      : activeTab === 'past'
        ? isLoadingPast
        : isLoadingGoing;

  const userIsAdmin = isAdmin();
  const isSearchMode = searchQuery.length > 0;
  const activeTabMeta =
    activeTab === 'upcoming'
      ? {
          loadedItemCount: upcomingEvents.length,
          loadedPageCount: upcomingPageCount,
          hasNextPage: hasNextUpcoming ?? false,
        }
      : activeTab === 'past'
        ? {
            loadedItemCount: pastEvents.length,
            loadedPageCount: pastPageCount,
            hasNextPage: hasNextPast ?? false,
          }
        : {
            loadedItemCount: goingEvents.length,
            loadedPageCount: goingPageCount,
            hasNextPage: hasNextGoing ?? false,
          };

  useEventsListScrollRestoration({
    activeTab: activeTab as EventsListScrollTab,
    isSearchMode,
    isLoadingActiveTab: isLoading,
    activeLoadedItemCount: activeTabMeta.loadedItemCount,
    activeLoadedPageCount: activeTabMeta.loadedPageCount,
    activeHasNextPage: activeTabMeta.hasNextPage,
  });

  // Handle search state changes from EventsSearch component
  const handleSearchChange = useCallback(
    (query: string, results: Event[], loading: boolean) => {
      setSearchQuery(query);
      setSearchResults(results);
      setIsSearchLoading(loading);
    },
    []
  );

  const handleEventNavigate = useCallback(
    (eventId: number) => {
      if (isSearchMode) {
        return;
      }

      const scrollContainer = getAppScrollContainer();
      if (!scrollContainer) {
        return;
      }

      writeEventsListScrollSnapshot({
        version: 1,
        mode: 'tabs',
        tab: activeTab as EventsListScrollTab,
        pathname: window.location.pathname,
        historyIndex: getCurrentHistoryIndex(),
        scrollTop: Math.max(0, scrollContainer.scrollTop),
        loadedItemCount: activeTabMeta.loadedItemCount,
        loadedPageCount: activeTabMeta.loadedPageCount,
        clickedEventId: eventId,
        timestamp: Date.now(),
      });
    },
    [
      activeTab,
      activeTabMeta.loadedItemCount,
      activeTabMeta.loadedPageCount,
      isSearchMode,
    ]
  );

  return (
    <div className="w-full max-w-4xl mx-auto px-4 pb-4 mt-[34px]">
      <h1 className="mb-6">Events</h1>
      <div className="flex gap-2 mb-6">
        <div className="flex-1">
          <EventsSearch onSearchChange={handleSearchChange} />
        </div>
        {userIsAdmin && (
          <Button
            className="bg-purple-400 text-white whitespace-nowrap rounded-lg h-12 px-4"
            onClick={() => navigate({ to: '/events/new' })}
            data-testid="new-event-button"
          >
            <PlusIcon size={20} weight="bold" />
            New Event
          </Button>
        )}
      </div>

      {isSearchMode ? (
        // Search results view
        <div>
          {isSearchLoading ? (
            <EventsListSkeleton />
          ) : (
            <EventsList
              events={searchResults}
              userId={user?.id || null}
              isPast={false}
            />
          )}
        </div>
      ) : (
        // Normal tabs view
        <Tabs
          value={activeTab}
          onValueChange={(value) =>
            navigate({
              to: '/events',
              search: { tab: value as 'upcoming' | 'past' | 'going' },
            })
          }
          className="w-full"
        >
          <TabsList className="grid w-full grid-cols-3 mb-6">
            <TabsTrigger value="upcoming">Upcoming</TabsTrigger>
            <TabsTrigger value="going">Going</TabsTrigger>
            <TabsTrigger value="past">Past</TabsTrigger>
          </TabsList>

          <TabsContent value="upcoming" className="mt-0">
            {isLoading ? (
              <EventsListSkeleton />
            ) : (
              <EventsList
                events={upcomingEvents}
                userId={user?.id || null}
                isPast={false}
                onEventNavigate={handleEventNavigate}
                onLoadMore={() => fetchNextUpcoming()}
                hasNextPage={hasNextUpcoming}
                isFetchingNextPage={isFetchingNextUpcoming}
              />
            )}
          </TabsContent>

          <TabsContent value="going" className="mt-0">
            {isLoading ? (
              <EventsListSkeleton />
            ) : (
              <EventsList
                events={goingEvents}
                userId={user?.id || null}
                isPast={false}
                onEventNavigate={handleEventNavigate}
                onLoadMore={() => fetchNextGoing()}
                hasNextPage={hasNextGoing}
                isFetchingNextPage={isFetchingNextGoing}
              />
            )}
          </TabsContent>

          <TabsContent value="past" className="mt-0">
            {isLoading ? (
              <EventsListSkeleton />
            ) : (
              <EventsList
                events={pastEvents}
                userId={user?.id || null}
                isPast={true}
                onEventNavigate={handleEventNavigate}
                onLoadMore={() => fetchNextPast()}
                hasNextPage={hasNextPast}
                isFetchingNextPage={isFetchingNextPast}
              />
            )}
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
